import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  MouseEventHandler,
  RefObject,
  useMemo,
} from "react";
import { v4 as uuid } from "uuid";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";

const Label = styled.label<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
`;

const Icon = styled(FontAwesomeIcon)<{ $visible: boolean }>`
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid ${(props) => props.theme.colors.grays4};
  background-color: ${(props) => props.theme.colors.grays9};
  border-radius: ${(props) => props.theme.borderRadius.small};
  cursor: pointer;

  ${HiddenCheckbox}:disabled + & {
    opacity: 0.7;
    cursor: not-allowed;
  }

  ${HiddenCheckbox}:checked + & {
    background-color: ${(props) => props.theme.colors.primary4};
    border-color: ${(props) => props.theme.colors.primary4};
    color: ${(props) => props.theme.colors.white};
  }

  ${HiddenCheckbox}:hover:not(:checked):not(:disabled) + & {
    background-color: ${(props) => props.theme.colors.misc3};
    border-color: ${(props) => props.theme.colors.grays3};
  }
`;

type CheckboxProps = DetailedHTMLProps<
  HTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  className?: string;
  disabled?: boolean;
  checked: boolean;
  visible?: boolean;
  readOnly?: boolean;
  id?: string;
  onClick?: MouseEventHandler<HTMLLabelElement>;
  bypassPreventDefault?: boolean;
  ref?: RefObject<HTMLInputElement> | null;
};

export default function Checkbox({
  className,
  checked,
  id,
  onClick,
  disabled = false,
  readOnly = false,
  visible = true,
  bypassPreventDefault = false,
  ...props
}: CheckboxProps): JSX.Element {
  const fallbackId = useMemo(() => `checkbox-${uuid()}`, []);
  const inputId = id ?? fallbackId;

  const onClickHandler: MouseEventHandler<HTMLLabelElement> = (event) => {
    if (!bypassPreventDefault) {
      event.preventDefault();
    }

    onClick?.(event);
  };

  return (
    <Label
      $visible={visible}
      htmlFor={inputId}
      className={className}
      onClick={onClickHandler}
    >
      <HiddenCheckbox
        disabled={disabled}
        readOnly={readOnly}
        id={inputId}
        checked={checked}
        {...props}
      />
      <StyledCheckbox checked={checked}>
        <Icon $visible={checked} icon={faCheck} size="sm" />
      </StyledCheckbox>
    </Label>
  );
}
